const MODULE_NAME = "_13_current_week_largest_open_pos_table";

import React, { useEffect, useState } from "react";
import axios from "axios";
import { Tooltip } from "./tooltips.js";
import { Link, useLocation } from "react-router-dom";
import { useDateRange } from "./dateRangeContext.js";
import { useTagFilter } from "./TagFilterContext.js";
import { analyticsHandler } from "./eventLogger.js";
import { FullscreenIcon, getFullscreenContainerStyles, getFullscreenHeaderStyles, getFullscreenLayoutStyles, useFullscreenAttribute } from "./fullscreenComponentUtils.js";
import { useModuleVisibility, getActiveModule } from "./useModuleVisibility.js";

const printdebug = process.env.REACT_APP_DEBUG === 'true';
const debugprefix = `[${MODULE_NAME}]`;

// Centralized style for table
const tableStyle = {
    width: "100%",  
    borderCollapse: "collapse",
    marginTop: 0,
    background: "#222",
};

const thStyle = {
    padding: "6px 8px",
    fontSize: 15,
    textAlign: "left",
    borderBottom: "1px solid #eee",
    cursor: "pointer",
    userSelect: "none",
    backgroundColor: "#333",
    transition: "background-color 0.2s"
};

const thHoverStyle = {
    ...thStyle,
    backgroundColor: "#444"
};

const thActiveSortStyle = {
    ...thStyle,
    backgroundColor: "#FF8C00",
    color: "#fff",
    fontWeight: 700,
    padding: "6px 8px"
};

const tdStyle = {
    padding: "6px 8px",
};

const tfootTdStyle = {
    fontWeight: 700,
    color: "#228B22",
    padding: "6px 8px",
    borderTop: "2px solid #222",
};

const API_BASE = process.env.REACT_APP_API_URL || "https://pro.tontradingtools.com";
const API_URL = `${API_BASE.replace(/\/+$/, "")}/api/largest_open_pos_table`;

// Helper: Strip time and timezone from endpoint date string
function stripTimeZone(dateStr) {
    if (!dateStr || typeof dateStr !== "string") return dateStr;
    return dateStr.replace(/\s\d{2}:\d{2}:\d{2}.*$/, "");
}

// Helper: Convert "Mon, 06 Oct 2025" to "YYYY-MM-DD"
function toIsoDateFromEndpoint(dateStr) {
    if (!dateStr || typeof dateStr !== "string") return "";
    const cleanDate = stripTimeZone(dateStr).trim();
    const match = cleanDate.match(/^\w{3},\s(\d{2})\s(\w{3})\s(\d{4})$/);
    if (match) {
        const [, day, monthStr, year] = match;
        const months = {
            Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
            Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12"
        };
        const month = months[monthStr] || "01";
        return `${year}-${month}-${day}`;
    }
    const d = new Date(cleanDate);
    if (!isNaN(d)) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    }
    return cleanDate;
}

// For display: "MM/DD/YY"
function formatAPIDate(dateStr) {
    const cleanDate = stripTimeZone(dateStr);
    const match = cleanDate.match(/^\w{3},\s(\d{2})\s(\w{3})\s(\d{4})$/);
    if (match) {
        const [, day, monthStr, year] = match;
        const months = {
            Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
            Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12"
        };
        const mm = months[monthStr] || "01";
        return `${mm}/${day}/${year.slice(-2)}`;
    }
    const d = new Date(cleanDate);
    if (!isNaN(d)) {
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const yy = String(d.getFullYear()).slice(-2);
        return `${mm}/${dd}/${yy}`;
    }
    return cleanDate;
}

function formatCurrency(val, digits = 2) {
    if (val === "N/A" || val === null || val === undefined || isNaN(val)) return val;
    val = parseFloat(val);
    return `$${val.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

function colorPL(val, digits = 2) {
    if (val === "N/A" || val === null || val === undefined || isNaN(val)) return val;
    val = parseFloat(val);
    let color = "#222";
    if (val > 0) color = "#228B22";
    if (val < 0) color = "#B22222";
    return (
        <span style={{ color, fontWeight: 500 }}>
            {val < 0
                ? `-$${Math.abs(val).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`
                : `$${val.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`
            }
        </span>
    );
}

// Sorting helper
function sortData(data, sortCol, sortDir) {
    if (!sortCol) return data;
    
    const sorted = [...data].sort((a, b) => {
        let aVal = a[sortCol];
        let bVal = b[sortCol];
        
        // Handle numeric values
        if (!isNaN(aVal) && !isNaN(bVal) && aVal !== "" && bVal !== "") {
            aVal = parseFloat(aVal);
            bVal = parseFloat(bVal);
            return sortDir === "asc" ? aVal - bVal : bVal - aVal;
        }
        
        // Handle string values
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
        
        if (sortDir === "asc") {
            return aVal.localeCompare(bVal);
        } else {
            return bVal.localeCompare(aVal);
        }
    });
    
    return sorted;
}

// Sorting indicator component
function SortIndicator({ column, sortCol, sortDir }) {
    if (column !== sortCol) return null;
    return (
        <span style={{ marginLeft: "6px", fontSize: "12px" }}>
            {sortDir === "asc" ? "▲" : "▼"}
        </span>
    );
}

export default function LargestOpenPosTable(props) {
    const effectiveDate = props.activeDate || null;
    const { globalTagFilter } = useTagFilter();
    const { windowDays } = useDateRange();

    // Check if in fullscreen mode
    const isVisible = useModuleVisibility(MODULE_NAME);
    const isFullscreen = getActiveModule() === MODULE_NAME;

    // Use centralized hook for fullscreen attribute
    useFullscreenAttribute(isFullscreen);

    // Set fullscreen indicator on mount/unmount
    useEffect(() => {
        if (isFullscreen) {
            document.documentElement.setAttribute('data-fullscreen', 'true');
            return () => document.documentElement.removeAttribute('data-fullscreen');
        }
    }, [isFullscreen]);

    // Sync with global filter
    const [tagFilter, setTagFilter] = useState(globalTagFilter);
    
    // Sorting state
    const [sortCol, setSortCol] = useState(null);
    const [sortDir, setSortDir] = useState("asc");
    
    // Collapsible state
    const [collapsed, setCollapsed] = useState(false);

    useEffect(() => {
        setTagFilter(globalTagFilter);
    }, [globalTagFilter]);

    const {
        apiUrl = API_URL,
        screen,
        refreshKey,
        data: propData,
        dateCol: propDateCol,
        tooltip: propTooltip,
        onDateChange
    } = props;

    // State for data
    const [data, setData] = useState(Array.isArray(propData) ? propData : []);
    const [dateCol, setDateCol] = useState(propDateCol || "Date");
    const [error, setError] = useState("");
    const [tooltip, setTooltip] = useState(propTooltip || "");
    const [tagDefs, setTagDefs] = useState({});
    const [loading, setLoading] = useState(false);

    // --- Fetch for Largest Position ---
    useEffect(() => {
        if (!effectiveDate) {
            setError("No reporting date available.");
            setData([]);
            return;
        }
        setLoading(true);

        const storedUserKey = sessionStorage.getItem("userKey");
        
        // Create the payload object
        const payload = {
            window_days: windowDays,
            active_date: effectiveDate,
            refreshKey
        };
        
        if (tagFilter) payload.tag_filter = tagFilter;
        if (storedUserKey) payload.userKey = storedUserKey;

        if (printdebug) console.log(`${debugprefix} POSTing to: ${apiUrl}`, payload);

        axios.post(apiUrl, payload, { withCredentials: true })
            .then((res) => {
                if (printdebug) console.log(`${debugprefix} Response status: ${res.status}`);
                const apiData = Array.isArray(res.data.data) ? res.data.data : [];
                setData(apiData);
                setDateCol(res.data.date_col || "Date");
                setTooltip(res.data.tooltip || res.data.intraday_risk_chart || "");
                setTagDefs(res.data.recommended_tag_definitions || {});
                setError("");
                setSortCol(null);
                setSortDir("asc");
            })
            .catch((err) => {
                const errorMsg = err.response?.data?.error || err.message || "Failed to load largest open position data.";
                // Only show errors that aren't "No trades found" (which are already handled)
                if (!errorMsg.includes("No trades with that tag")) {
                    setError(errorMsg);
                }
                if (printdebug) {
                    console.error(`${debugprefix} Fetch Error:`, errorMsg);
                    console.error(`${debugprefix} Full Error Object:`, err);
                }
            })
            .finally(() => setLoading(false));
    }, [windowDays, effectiveDate, apiUrl, refreshKey, tagFilter]);

    // Define the order of columns
    const columnOrder = [
        dateCol,
        "Symbol",
        "Max Qty",
        "Price",
        "Price range",
        "Risk %",
        "Symbol profit",
        "Open Orders"
        
    ];

    const friendlyColumns = {
        [dateCol]: windowDays === "90" ? "Week" : "Day",
        Symbol: "Stock",
        "Max Qty": "Max Qty",
        Price: "Price",
        "Price range": "Price Range",
        "Risk %": "Risk %",
        "Symbol profit": "Day P/L*",
        "Open Orders": "Open Orders",
    };

    const isMobile = window.innerWidth <= 700;
    const digits = isMobile ? 0 : 2;
    const mobileHiddenCols = ["Max Qty", "Price range"];

    // Calculate totalPL directly from data
    const totalPL = Array.isArray(data)
        ? data.reduce((sum, row) => {
            let val = row["Symbol profit"];
            if (typeof val !== "number") val = parseFloat(val);
            return sum + (isNaN(val) ? 0 : val);
        }, 0)
        : 0;

    // Only show columns in columnOrder, in that order, and filter out mobile-hidden columns
    const visibleColumns = columnOrder.filter(
        col => data.length > 0 &&
            Object.keys(data[0]).includes(col) &&
            !(isMobile && mobileHiddenCols.includes(col)) &&
            col !== "Recommended Tag Definition"
    );

    // Handle column sort
    const handleSort = (col) => {
        if (sortCol === col) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortCol(col);
            setSortDir("asc");
        }
    };

    // Sort the data for display
    const sortedData = sortData(data, sortCol, sortDir);

    // Return null if component is not visible
    if (!isVisible) return null;

    // --- UI ---
    return (
        <div
            className={`collapse-toggle-container${collapsed ? " collapsed" : ""}`}
            style={getFullscreenContainerStyles(isFullscreen)}
            data-analytics={MODULE_NAME}
        >
            <div style={getFullscreenHeaderStyles(isFullscreen)}>
                <h4
                    className="collapse-toggle-headline"
                    onClick={() => setCollapsed(!collapsed)}
                    style={{ cursor: "pointer", userSelect: "none", marginTop: 0, marginBottom: collapsed ? 0 : 16, flex: 1 }}
                >
                    📊 Largest intra day position table – {windowDays === "90" ? "Week" : "Day"} (last {windowDays} days)
                    <Tooltip text={tooltip} />
                </h4>
                {!isFullscreen && (
                    <FullscreenIcon 
                        moduleName={MODULE_NAME}
                        title="Largest Open Position Table"
                        additionalParams={{ windowDays }}
                    />
                )}
            </div>

            {!collapsed && (
                <>
                    {/* Tag filter info */}
                    {tagFilter && (
                        <div style={{ marginBottom: 12, padding: "8px 12px", background: "#2d5f7f", borderRadius: 4, fontSize: 13, color: "#ccc" }}>
                            🏷️ Filtering by tag: <strong>"{tagFilter}"</strong>
                        </div>
                    )}

                    {loading && <div>Loading...</div>}
                    {error && <div style={{ color: "#e63946", marginBottom: 12 }}>{error}</div>}
                    {data.length === 0 && !loading && !error && (
                        <div style={{ color: "#888", padding: 16 }}>No data available{tagFilter ? ` for tag "${tagFilter}"` : ""}.</div>
                    )}
                    {data.length > 0 && (
                        <div className="responsive-table-wrapper" style={{...getFullscreenLayoutStyles(isFullscreen), width: "100%"}}>
                            <table className="responsive-table" style={{...tableStyle, width: "100%"}}>
                                <thead>
                                    <tr>
                                        {visibleColumns.map(col =>
                                            <th 
                                                key={col} 
                                                style={sortCol === col ? thActiveSortStyle : thStyle}
                                                onMouseEnter={(e) => {
                                                    if (sortCol !== col) {
                                                        e.target.style.backgroundColor = "#444";
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (sortCol !== col) {
                                                        e.target.style.backgroundColor = "#333";
                                                    }
                                                }}
                                                onClick={() => handleSort(col)}
                                                title="Click to sort"
                                            >
                                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                    {friendlyColumns[col] || col}
                                                    <SortIndicator column={col} sortCol={sortCol} sortDir={sortDir} />
                                                </div>
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedData.map((row, idx) => {
                                        const isoDate = toIsoDateFromEndpoint(row[dateCol]);
                                        const symbol = row["Symbol"] || "N/A";
                                        const maxQty = row["Max Qty"] || 0;

                                        return (
                                            <tr key={idx} className="largest-open-pos-row">
                                                {visibleColumns.map(col => {
                                                    if (col === "Recommended Tag") {
                                                        const tag = row[col];
                                                        const def = tagDefs[tag] || "";
                                                        const tagColor = tag === "highConvict" ? "#e63946" : undefined;
                                                        return (
                                                            <td key={col} style={{ ...tdStyle, color: tagColor, fontWeight: tag === "highConvict" ? 700 : undefined }}>
                                                                <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                                                    {tag}
                                                                    <Tooltip text={def}>
                                                                        <span className="tooltip-icon" style={{ marginLeft: 4, color: "#888" }}>🛈</span>
                                                                    </Tooltip>
                                                                </span>
                                                            </td>
                                                        );
                                                    }
                                                    if (col === "Trade Tag") {
                                                        return (
                                                            <td key={col} style={tdStyle}>
                                                               
                                                            </td>
                                                        );
                                                    }
                                                    if (col.toLowerCase().includes("date") || col.toLowerCase().includes("week")) {
                                                        return (
                                                            <td key={col} style={tdStyle}>
                                                                <Link
                                                                    to={{
                                                                        pathname: "/details",
                                                                        search: `?date=${isoDate}&symbol=${encodeURIComponent(symbol)}&qty=${encodeURIComponent(maxQty)}`
                                                                    }}
                                                                    style={{ color: "#0077cc", textDecoration: "underline", cursor: "pointer" }}
                                                                    title="View details for this date"
                                                                    onClick={(e) => {
                                                                        sessionStorage.setItem("navigationDate", isoDate);
                                                                        analyticsHandler({
                                                                            event_type: "link_click",
                                                                            module_name: MODULE_NAME,
                                                                            subcomponent: "date-details-link",
                                                                            screen: props.screen,
                                                                            metadata: { date: isoDate, symbol, maxQty }
                                                                        })(e);
                                                                        if (props.onNavigate) {
                                                                            props.onNavigate("pg_details", isoDate);
                                                                        }
                                                                    }}
                                                                >
                                                                    {formatAPIDate(row[col])}
                                                                </Link>
                                                            </td>
                                                        );
                                                    }
                                                    if (col === "Symbol profit" || col.toLowerCase().includes("profit")) {
                                                        return (
                                                            <td key={col} style={tdStyle}>
                                                                {colorPL(row[col], digits)}
                                                            </td>
                                                        );
                                                    }
                                                    if (col === "Price" || col.toLowerCase().includes("balance")) {
                                                        return (
                                                            <td key={col} style={tdStyle}>
                                                                {formatCurrency(row[col], digits)}
                                                            </td>
                                                        );
                                                    }
                                                    if (col.toLowerCase().includes("risk")) {
                                                        let val = row[col];
                                                        if (val === "N/A" || val === null || val === undefined || isNaN(val)) {
                                                            return (
                                                                <td key={col} style={tdStyle}>{val}</td>
                                                            );
                                                        }
                                                        val = parseFloat(val);
                                                        return (
                                                            <td key={col} style={tdStyle}>
                                                                {`${Math.round(val)}%`}
                                                            </td>
                                                        );
                                                    }
                                                    return (
                                                        <td key={col} style={tdStyle}>
                                                            {row[col]}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td
                                            colSpan={visibleColumns.length - 1}
                                            style={{
                                                textAlign: "right",
                                                fontWeight: 600,
                                                padding: "6px 8px",
                                                borderTop: "2px solid #222"
                                            }}
                                        >
                                            Total P/L:
                                        </td>
                                        <td style={{
                                            ...tfootTdStyle,
                                            color: totalPL < 0 ? "#B22222" : "#228B22"
                                        }}>
                                            {totalPL < 0
                                                ? `-$${Math.abs(totalPL).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`
                                                : `$${totalPL.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                            <div style={{
                                color: "#bbb",
                                fontSize: 13,
                                fontStyle: "italic",
                                marginTop: 8,
                                marginBottom: 4,
                                textAlign: "left",
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                                overflowWrap: "break-word"
                            }}>
                                * daily symbol profit/loss may be different than total profit for the entire trade
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// Tab style improvements
const tabBaseStyle = {
    padding: "10px 20px",
    cursor: "pointer",
    border: "1px solid #007bff",
    borderBottom: "none",
    borderRadius: "8px 8px 0 0",
    marginRight: 4,
    background: "#f4f8fb",
    color: "#007bff",
    fontWeight: 500,
    transition: "background 0.2s, color 0.2s"
};

const tabActiveStyle = {
    ...tabBaseStyle,
    background: "#007bff",
    color: "#fff",
    fontWeight: 700,
    boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
    zIndex: 2
};

const tabInactiveStyle = {
    ...tabBaseStyle,
    background: "#e9ecef",
    color: "#007bff",
    fontWeight: 500,
    opacity: 0.85,
    zIndex: 1
};
